import { useEffect, useRef, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { Lock, PaperPlaneRight } from "@phosphor-icons/react";
import { AttachmentChosen, AttachmentPicker } from "../components/AttachmentComposer";
import type { AttachmentUploadFailure } from "../data/attachmentQueries";
import { useFinePointer } from "../lib/useFinePointer";
import { useViewOnly } from "../data/readSource";

/**
 * Writing to Petrovic, in one row: attach, write, send. `prototype/kund/pages.js` `composer()`, the
 * second build round of 2026-09-24, shared by the client's Chatt and the comment on one request so
 * the two places a client writes look like one thing.
 *
 * In Läsläge the whole row is replaced by one locked line. It used to be said twice, in the
 * placeholder of a disabled box and again in a notice under it (FEATURE-MAP, "Numbers shown more
 * than once", item 11), and a disabled box a person cannot type in is a control that looks live.
 *
 * The count of characters is shown only once the text nears the ceiling. Below that it is a number
 * that asks nothing of anyone, which is design.md section 3, rule 2. The ceiling itself is still the
 * textarea's `maxLength`, so the browser refuses the 5 001st character whether or not the count is
 * on screen.
 *
 * The box grows with what is typed, to a limit, because a one-line box that hides the second line of
 * a message is a box nobody can proofread. The height is set through the CSSOM, which the portal's
 * CSP allows; only inline `style` attributes and injected `<style>` elements are refused.
 *
 * Enter sends in the chat since 2026-10-07 (build round 16), Mattej's decision through the planning
 * chat, and only where the primary pointer is fine: on a computer Enter sends and Shift+Enter starts
 * a new line, and on a phone Enter keeps starting a new line and the button sends, as phones do. It
 * is opt-in through `enterSends`, because the decision was about the chat. The comment on one request
 * keeps Enter as a new line, and Petrovic's Konversation has a box of its own and is not this one.
 * The send goes through the form, so the button's rule, the page's own `onSubmit` and its pending
 * state are the only way a message leaves, whichever key or button started it.
 */
export interface ComposerAttachments {
  inputId: string;
  files: readonly File[];
  onChange: (next: File[]) => void;
  sendFailures?: readonly AttachmentUploadFailure[];
}

export function Composer({
  id,
  label,
  value,
  onChange,
  onSubmit,
  placeholder,
  maxLength,
  pending,
  canSubmit,
  locked = false,
  lockedText = "Läsläge. Det går inte att skriva just nu.",
  submitLabel = "Skicka",
  pendingLabel = "Skickar",
  attachments,
  enterSends = false,
  foot,
  errors,
  testId,
  className
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  placeholder?: string;
  maxLength?: number;
  pending: boolean;
  /** Whether there is something to send. The pending and locked states are added here. */
  canSubmit: boolean;
  locked?: boolean;
  lockedText?: string;
  submitLabel?: string;
  pendingLabel?: string;
  attachments?: ComposerAttachments;
  /**
   * Enter sends, on a fine pointer only. The client's Chatt passes it; everything else leaves it off
   * and Enter writes a new line everywhere.
   */
  enterSends?: boolean;
  /** Under the row: the line about sending a secret instead. */
  foot?: ReactNode;
  /** The refusals of the last send, each its own `role="alert"` paragraph. */
  errors?: ReactNode;
  testId?: string;
  className?: string;
}) {
  const box = useRef<HTMLTextAreaElement | null>(null);
  const finePointer = useFinePointer();
  const viewOnly = useViewOnly();
  const sendsOnEnter = enterSends && finePointer;
  // Set when Enter sent the message, read once the send is over.
  const refocusAfterSend = useRef(false);

  useEffect(() => {
    const element = box.current;
    if (!element) {
      return;
    }
    element.style.height = "auto";
    element.style.height = `${Math.min(element.scrollHeight, 192)}px`;
  }, [value]);

  /*
    The box is disabled while a message is on its way, and Chromium moves focus off a disabled
    element to the page and does not give it back when the element is enabled again (measured in
    headless Chromium on 2026-10-07). Somebody who sent with Enter has their hands on the keys and
    goes on typing, into nothing, so the box takes focus back once the send is over. Only after
    Enter, since a finger on the send button is a phone, where focus would raise the keyboard
    unasked, and only when focus is nowhere, since a person who clicked somewhere else while the
    message was on its way keeps it where they put it.
  */
  useEffect(() => {
    if (pending || !refocusAfterSend.current) {
      return;
    }
    refocusAfterSend.current = false;
    const active = document.activeElement;
    if (box.current && (active === null || active === document.body)) {
      box.current.focus();
    }
  }, [pending]);

  // "Visa som kund" (build round 41): nothing that writes renders. A locked composer is the client's
  // own sentence about a paused portal, not a control, so the view shows it as the client sees it.
  if (viewOnly && !locked) {
    return null;
  }

  if (locked) {
    return (
      <div className={`k-composer is-locked${className ? ` ${className}` : ""}`} role="status" data-testid="composer-lock">
        <Lock size={18} aria-hidden="true" />
        <span>{lockedText}</span>
      </div>
    );
  }

  const nearCeiling = maxLength !== undefined && value.length >= Math.floor(maxLength * 0.9);

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    // Shift+Enter is the new line. Ctrl, Alt and Meta with Enter belong to the browser, the
    // operating system or an extension, and are left to them untouched.
    if (!sendsOnEnter || event.key !== "Enter" || event.shiftKey) {
      return;
    }
    if (event.ctrlKey || event.altKey || event.metaKey) {
      return;
    }
    // An input method, for Japanese, Chinese or Korean among others, uses Enter to accept the word
    // being composed, and that Enter is not a send. Safari reports the accepting keydown with
    // `isComposing` already false and keyCode 229, so both are asked.
    if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) {
      return;
    }

    // From here Enter never writes a line break, whether or not anything is sent.
    event.preventDefault();

    // The button's rule, and on top of it the decision's own: text that is only spaces and line
    // breaks is not a message. A file alone is, as it is for the button.
    const somethingToSend = value.trim().length > 0 || (attachments?.files.length ?? 0) > 0;
    if (pending || !canSubmit || !somethingToSend) {
      return;
    }

    const form = event.currentTarget.form;
    if (!form) {
      return;
    }
    refocusAfterSend.current = true;
    // Every browser the build targets has `requestSubmit` (Safari since 16). The fallback is the
    // same event without the browser's constraint check, which this form does not rely on.
    if (typeof form.requestSubmit === "function") {
      form.requestSubmit();
    } else {
      form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
    }
  }

  return (
    <form className={`k-composer${className ? ` ${className}` : ""}`} onSubmit={onSubmit} data-testid={testId}>
      <div className={`k-composer-row${attachments ? "" : " is-plain"}`}>
        {attachments ? (
          <AttachmentPicker
            compact
            inputId={attachments.inputId}
            files={attachments.files}
            onChange={attachments.onChange}
            disabled={pending}
          />
        ) : null}
        <label className="visually-hidden" htmlFor={id}>
          {label}
        </label>
        <textarea
          id={id}
          ref={box}
          rows={1}
          maxLength={maxLength}
          value={value}
          disabled={pending}
          placeholder={placeholder}
          aria-keyshortcuts={sendsOnEnter ? "Enter" : undefined}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={onKeyDown}
        />
        <button type="submit" className="button is-primary k-send" disabled={pending || !canSubmit}>
          <PaperPlaneRight size={18} aria-hidden="true" />
          <span className="k-send-label">{pending ? pendingLabel : submitLabel}</span>
        </button>
      </div>

      {attachments ? (
        <AttachmentChosen
          inputId={attachments.inputId}
          files={attachments.files}
          onChange={attachments.onChange}
          disabled={pending}
          sendFailures={attachments.sendFailures}
        />
      ) : null}

      {nearCeiling ? (
        <p className="k-composer-count" data-testid="composer-count">
          {value.length} / {maxLength}
        </p>
      ) : null}

      {foot}
      {errors}
    </form>
  );
}
